import { useCallback, useMemo, useState } from 'react';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import { addMonths } from 'date-fns/addMonths';
import { startOfMonth } from 'date-fns/startOfMonth';
import { useTranslation } from 'react-i18next';
import {
  DataGridV2,
  StatusChip,
  type DataGridV2Column,
  type DataGridV2LegendItem,
  type DataGridV2QuickFilter,
  type DataGridV2RowAction,
} from '@bloomays-lib/ui.shared/datagrid';
import { convertDateToStringFormat } from '@bloomays-lib/utils.shared';
import { PurchaseOrderMissionCoverage } from '@bloomays-lib/types.shared';
import { CoverageMonthTimeline } from '../molecules/CoverageMonthTimeline';
import {
  accountManagerInitials,
  COVERAGE_MONTH_OFFSET,
  COVERAGE_MONTH_SLOT_KEYS,
  CoverageMonthSegmentState,
  DashboardPurchaseOrder,
  formatAccountManagerShort,
  formatMissionPeriod,
  MissionEnrichment,
  resolveCoverageMonthSegmentState,
} from '../../helpers/purchaseOrderDashboard';
import { PurchaseOrderCoverageDetailPanel } from './PurchaseOrderCoverageDetailPanel';

export type PurchaseOrderCoverageTabProps = {
  coverages: PurchaseOrderMissionCoverage[];
  purchaseOrders: DashboardPurchaseOrder[];
  missionEnrichmentByRecordId: Record<string, MissionEnrichment>;
  referenceDate: Date;
  requestedCoverageFilter?: 'gap';
  onRequestedCoverageFilterChange?: (filter: 'gap' | undefined) => void;
  onCreateBdc: (coverage: PurchaseOrderMissionCoverage) => void;
  onEditPurchaseOrder: (purchaseOrder: DashboardPurchaseOrder) => void;
};

type CoverageRow = {
  id: string;
  missionLabel: string;
  missionPeriod: string;
  clientName: string;
  bloomerName: string;
  amShort: string;
  amInitials: string;
  segments: CoverageMonthSegmentState[];
  monthOffsets: number[];
  hasCoverageGap: boolean;
  raw: PurchaseOrderMissionCoverage;
};

export const PurchaseOrderCoverageTab = ({
  coverages,
  purchaseOrders,
  missionEnrichmentByRecordId,
  referenceDate,
  requestedCoverageFilter,
  onRequestedCoverageFilterChange,
  onCreateBdc,
  onEditPurchaseOrder,
}: PurchaseOrderCoverageTabProps) => {
  const { t } = useTranslation(['purchaseOrderDashboard']);
  const [selectedCoverage, setSelectedCoverage] = useState<PurchaseOrderMissionCoverage | undefined>(undefined);

  const coverageTimelineHeader = useMemo(() => {
    const from = convertDateToStringFormat(startOfMonth(referenceDate), 'MMM yy');
    const to = convertDateToStringFormat(startOfMonth(addMonths(referenceDate, 3)), 'MMM yy');
    return t('colCoverageTimeline', { from, to });
  }, [referenceDate, t]);

  const rows = useMemo<CoverageRow[]>(
    () =>
      coverages.map((coverage) => {
        const enrichment = missionEnrichmentByRecordId[coverage.missionRecordId];
        const monthOffsets = COVERAGE_MONTH_SLOT_KEYS.map((key) => COVERAGE_MONTH_OFFSET[key]);
        const segments = COVERAGE_MONTH_SLOT_KEYS.map((key) =>
          resolveCoverageMonthSegmentState({
            missionRecordId: coverage.missionRecordId,
            beginMission: enrichment?.beginMission,
            endMission: enrichment?.endMission,
            purchaseOrders,
            referenceDate,
            monthOffset: COVERAGE_MONTH_OFFSET[key],
          }),
        );
        const accountManager = enrichment?.accountManager;
        return {
          id: coverage.missionRecordId,
          missionLabel: coverage.missionLabel ?? '',
          missionPeriod: formatMissionPeriod(enrichment?.beginMission, enrichment?.endMission),
          clientName: enrichment?.clientSocietyName ?? coverage.clientSocietyName ?? '—',
          bloomerName: enrichment?.bloomerFullName ?? '—',
          amShort: formatAccountManagerShort(accountManager),
          amInitials: accountManagerInitials(accountManager),
          segments,
          monthOffsets,
          hasCoverageGap: segments.some((state) => state === 'missing' || state === 'expiringSoon'),
          raw: coverage,
        };
      }),
    [coverages, missionEnrichmentByRecordId, purchaseOrders, referenceDate],
  );

  const coverageLegend = useMemo<DataGridV2LegendItem[]>(
    () => [
      { label: t('coverageLegendCovered'), tone: 'success' },
      { label: t('coverageLegendMissing'), tone: 'danger', variant: 'striped' },
      { label: t('coverageLegendExpiringSoon'), tone: 'warning' },
      { label: t('coverageLegendInactive'), color: '#C4C4CF' },
    ],
    [t],
  );

  const buildRowActions = useCallback(
    (row: CoverageRow): DataGridV2RowAction[] => {
      const actions: DataGridV2RowAction[] = [
        {
          label: t('coverageActionViewDetail'),
          icon: <OpenInNewOutlinedIcon fontSize="small" />,
          onClick: () => setSelectedCoverage(row.raw),
        },
      ];
      if (row.hasCoverageGap) {
        actions.push({
          label: t('createPurchaseOrder'),
          icon: <AddCircleOutlineIcon fontSize="small" />,
          divider: true,
          onClick: () => onCreateBdc(row.raw),
        });
      }
      return actions;
    },
    [onCreateBdc, t],
  );

  const quickFilterValues = useMemo(() => ({ coverageGap: requestedCoverageFilter ?? '' }), [requestedCoverageFilter]);

  const handleQuickFilterValuesChange = useCallback(
    (values: Record<string, string>) => {
      const filter = values.coverageGap;
      onRequestedCoverageFilterChange?.(filter === 'gap' ? 'gap' : undefined);
    },
    [onRequestedCoverageFilterChange],
  );

  const coverageQuickFilter = useMemo<DataGridV2QuickFilter<CoverageRow>>(
    () => ({
      field: 'coverageGap',
      label: t('coverageFilterLabel'),
      display: 'inline',
      allLabel: t('coverageFilterAll'),
      options: [
        { value: 'gap', label: t('coverageFilterWithGap'), tone: 'danger' },
        { value: 'ok', label: t('coverageFilterCovered'), tone: 'success' },
      ],
      predicate: (row, value) => (value === 'gap' ? row.hasCoverageGap : !row.hasCoverageGap),
    }),
    [t],
  );

  const columns = useMemo<DataGridV2Column<CoverageRow>[]>(
    () => [
      {
        field: 'missionLabel',
        header: t('colMission'),
        minWidth: 220,
        flex: 1,
        searchText: (row) => `${row.missionLabel} ${row.clientName} ${row.bloomerName} ${row.amShort}`,
        render: (row) => (
          <CellStack>
            <Typography variant="body2" fontWeight={600}>
              {row.missionLabel}
            </Typography>
            <SecondaryText variant="caption">{row.missionPeriod}</SecondaryText>
          </CellStack>
        ),
      },
      {
        field: 'clientName',
        header: t('colClientBloomer'),
        minWidth: 200,
        flex: 1,
        render: (row) => (
          <CellStack>
            <Typography variant="body2" fontWeight={600}>
              {row.clientName}
            </Typography>
            <SecondaryText variant="caption">{row.bloomerName}</SecondaryText>
          </CellStack>
        ),
      },
      {
        field: 'amShort',
        header: t('colAm'),
        width: 140,
        variant: 'avatar',
        avatar: (row) => ({
          initials: row.amInitials,
          name: row.amShort,
        }),
      },
      {
        field: 'segments',
        header: coverageTimelineHeader,
        minWidth: 240,
        flex: 1.2,
        sortable: false,
        render: (row) => (
          <CoverageMonthTimeline
            segments={row.segments}
            referenceDate={referenceDate}
            monthOffsets={row.monthOffsets}
          />
        ),
      },
      {
        field: 'hasCoverageGap',
        header: t('colCoverageState'),
        width: 150,
        sortable: false,
        render: (row) => (
          <StatusChip tone={row.hasCoverageGap ? 'danger' : 'success'} dot>
            {row.hasCoverageGap ? t('coverageStateGap') : t('coverageStateCovered')}
          </StatusChip>
        ),
      },
      {
        field: 'actions',
        header: t('colActions'),
        width: 120,
        variant: 'actions',
        actions: buildRowActions,
      },
    ],
    [buildRowActions, coverageTimelineHeader, referenceDate, t],
  );

  if (rows.length === 0) {
    return <EmptyText variant="body1Light">{t('emptyCoverage')}</EmptyText>;
  }

  return (
    <>
      <DataGridV2<CoverageRow>
        uniqueId="purchase-orders-coverage-v2"
        rows={rows}
        columns={columns}
        legend={coverageLegend}
        quickFilters={[coverageQuickFilter]}
        quickFilterValues={onRequestedCoverageFilterChange ? quickFilterValues : undefined}
        onQuickFilterValuesChange={onRequestedCoverageFilterChange ? handleQuickFilterValuesChange : undefined}
        search
        searchPlaceholder={t('searchMissingPlaceholder')}
        pagination
        pageSize={25}
        onRowClick={(row) => setSelectedCoverage(row.raw)}
        sx={{
          '& .MuiDataGrid-row': { cursor: 'pointer' },
        }}
      />

      <PurchaseOrderCoverageDetailPanel
        open={!!selectedCoverage}
        coverage={selectedCoverage}
        enrichment={selectedCoverage ? missionEnrichmentByRecordId[selectedCoverage.missionRecordId] : undefined}
        purchaseOrders={purchaseOrders}
        referenceDate={referenceDate}
        onClose={() => setSelectedCoverage(undefined)}
        onCreateBdc={(coverage) => {
          setSelectedCoverage(undefined);
          onCreateBdc(coverage);
        }}
        onEditPurchaseOrder={(purchaseOrder) => {
          setSelectedCoverage(undefined);
          onEditPurchaseOrder(purchaseOrder);
        }}
      />
    </>
  );
};

export default PurchaseOrderCoverageTab;

const CellStack = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  gap: 2,
}));

const SecondaryText = styled(Typography)(({ theme }) => ({
  color: theme.palette.neutral?.[60] ?? '#6b7280',
}));

const EmptyText = styled(Typography)(({ theme }) => ({
  color: theme.palette.neutral?.[60] ?? '#374151',
  fontStyle: 'italic',
  marginTop: 16,
}));
